import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * SceneBackground — pure CSS, low-distraction, per-section animated backgrounds.
 * Picks a scene based on which section is currently in view (IntersectionObserver
 * with sentinel divs). Smooth crossfade between scenes. No WebGL needed —
 * this stays in the page background and is calm enough not to compete with content.
 *
 * Scenes:
 *   - hero       : warm brass dawn (gold halo top, teal pool bottom)
 *   - manifesto  : sage parchment glow (left top), brass ember (right bottom)
 *   - wheel      : centered brass radial mandala
 *   - timeline   : horizon ribbon (slow horizontal sweep)
 *   - about      : moonlit teal (cool blue glow)
 *   - faq        : starfield (twinkle)
 *   - footer     : deep teal night (no halos)
 */
const SCENES = {
  hero: {
    bg:
      "radial-gradient(900px 600px at 50% 0%, rgba(216,185,122,0.32) 0%, transparent 60%)," +
      "radial-gradient(700px 460px at 85% 18%, rgba(82,124,120,0.30) 0%, transparent 65%)," +
      "radial-gradient(700px 480px at 50% 100%, rgba(46,78,75,0.55) 0%, transparent 70%)," +
      "linear-gradient(180deg, #142322 0%, #112020 60%, #0E1F1E 100%)",
  },
  manifesto: {
    bg:
      "radial-gradient(800px 560px at 16% 18%, rgba(216,185,122,0.22) 0%, transparent 60%)," +
      "radial-gradient(620px 460px at 86% 86%, rgba(82,124,120,0.40) 0%, transparent 65%)," +
      "linear-gradient(180deg, #122220 0%, #102020 100%)",
  },
  wheel: {
    bg:
      "radial-gradient(1100px 700px at 50% 50%, rgba(216,185,122,0.20) 0%, transparent 55%)," +
      "radial-gradient(700px 480px at 50% 18%, rgba(82,124,120,0.20) 0%, transparent 65%)," +
      "linear-gradient(180deg, #0F2120 0%, #0C1D1C 100%)",
  },
  timeline: {
    bg:
      "linear-gradient(180deg, rgba(82,124,120,0.18), rgba(82,124,120,0.04) 30%, rgba(46,78,75,0.30) 100%)," +
      "linear-gradient(180deg, #112221 0%, #0E1F1E 100%)",
  },
  about: {
    bg:
      "radial-gradient(900px 600px at 32% 38%, rgba(168,156,188,0.18) 0%, transparent 60%)," +
      "radial-gradient(700px 480px at 80% 70%, rgba(82,124,120,0.35) 0%, transparent 65%)," +
      "linear-gradient(180deg, #0F1F1F 0%, #0B1A1A 100%)",
  },
  faq: {
    bg:
      "radial-gradient(800px 560px at 70% 28%, rgba(216,185,122,0.18) 0%, transparent 60%)," +
      "linear-gradient(180deg, #0E1E1D 0%, #0A1818 100%)",
    stars: true,
  },
  footer: {
    bg: "linear-gradient(180deg, #0B1A1A 0%, #08141415 100%)",
  },
};

export function SceneAnchor({ id, children, className = "" }) {
  // Wrapper that exposes data-scene to the observer.
  return (
    <div data-scene={id} className={className}>{children}</div>
  );
}

export default function SceneBackground() {
  const [active, setActive] = useState("hero");
  const observed = useRef(null);

  useEffect(() => {
    const anchors = Array.from(document.querySelectorAll("[data-scene]"));
    if (!anchors.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        // Pick the entry closest to the upper half of the viewport
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => Math.abs(a.boundingClientRect.top - 120) - Math.abs(b.boundingClientRect.top - 120));
        if (visible[0]) setActive(visible[0].target.dataset.scene);
      },
      { threshold: [0.15, 0.35, 0.6], rootMargin: "-10% 0px -50% 0px" }
    );
    anchors.forEach((a) => obs.observe(a));
    observed.current = obs;
    return () => obs.disconnect();
  }, []);

  const scene = SCENES[active] || SCENES.hero;

  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      {/* scene crossfade — translucent so the celestial orbit shows through */}
      <AnimatePresence>
        <motion.div
          key={active}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.55 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: [0.7, 0, 0.2, 1] }}
          className="absolute inset-0"
          style={{ background: scene.bg, mixBlendMode: "multiply" }}
        />
      </AnimatePresence>

      {/* drifting brass dust on all scenes (subtle) */}
      <div
        className="absolute inset-0 opacity-25 mix-blend-screen animate-drift"
        style={{
          backgroundImage:
            "radial-gradient(circle at 22% 30%, rgba(216,185,122,0.18) 0px, transparent 2px)," +
            "radial-gradient(circle at 70% 60%, rgba(216,185,122,0.16) 0px, transparent 2px)," +
            "radial-gradient(circle at 45% 80%, rgba(216,185,122,0.14) 0px, transparent 2px)," +
            "radial-gradient(circle at 12% 76%, rgba(216,185,122,0.16) 0px, transparent 2px)," +
            "radial-gradient(circle at 90% 18%, rgba(216,185,122,0.14) 0px, transparent 2px)",
          backgroundSize: "640px 640px",
        }}
      />

      {/* twinkling stars only for FAQ */}
      {scene.stars && <StarField />}
    </div>
  );
}

function StarField() {
  // Use a stable seed (deterministic) so React Strict Mode + purity rules are happy.
  // 60 fixed stars, twinkling at varied offsets — pure CSS keyframes.
  const stars = React.useMemo(() => {
    // Mulberry32 PRNG (deterministic)
    let seed = 1729;
    const rand = () => {
      seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
      let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    return Array.from({ length: 60 }).map(() => ({
      x: rand() * 100,
      y: rand() * 100,
      size: 1 + rand() * 1.6,
      delay: rand() * 4,
      dur: 2.2 + rand() * 3,
      o: 0.4 + rand() * 0.45,
    }));
  }, []);
  return (
    <div className="absolute inset-0">
      {stars.map((s, i) => (
        <span
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${s.x}%`,
            top: `${s.y}%`,
            width: s.size,
            height: s.size,
            background: "#EFEAD8",
            boxShadow: "0 0 6px rgba(255,232,172,0.6)",
            animation: `twinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
            opacity: s.o,
          }}
        />
      ))}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.20; transform: scale(0.85); }
          50%      { opacity: 0.95; transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
}
